/**
 * useAnnouncements Hook
 * LAYER-SHARED-INTERACTIONS
 *
 * Manages announcement banner display and dismissal.
 * Filters announcements by role, route, and date.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { UserRole } from '../components/Shell/Shell';

export type AnnouncementType = 'info' | 'warning' | 'success' | 'error';

export interface Announcement {
  /** Unique identifier */
  id: string;
  /** Short title */
  title?: string;
  /** Announcement message */
  message: string;
  /** Visual type */
  type: AnnouncementType;
  /** Whether user can dismiss */
  dismissible: boolean;
  /** When to start showing */
  startDate?: string;
  /** When to stop showing */
  endDate?: string;
  /** Which roles should see this */
  targetRoles?: UserRole[];
  /** Which routes to show on (empty = all) */
  targetRoutes?: string[];
  /** Priority (higher = shown first) */
  priority?: number;
  /** Whether announcement is enabled */
  enabled: boolean;
  /** Optional link */
  link?: {
    text: string;
    url: string;
  };
}

export interface UseAnnouncementsOptions {
  /** Current user role */
  userRole?: UserRole;
  /** Current route/path */
  currentRoute?: string;
  /** Announcements data (can be passed in or fetched) */
  announcements?: Announcement[];
}

export interface UseAnnouncementsReturn {
  /** Active announcements to display */
  activeAnnouncements: Announcement[];
  /** Dismiss a single announcement */
  dismiss: (id: string) => void;
  /** Dismiss all current announcements */
  dismissAll: () => void;
  /** Check if an announcement is dismissed */
  isDismissed: (id: string) => boolean;
  /** Clear all dismissals (for testing) */
  clearDismissals: () => void;
}

const STORAGE_KEY = 'dismissed-announcements';

/**
 * Get dismissed announcement IDs from localStorage.
 */
function getDismissedIds(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch {
    // Ignore parsing errors
  }
  return new Set();
}

/**
 * Save dismissed announcement IDs to localStorage.
 */
function saveDismissedIds(ids: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Check if an announcement is currently active based on dates.
 */
function isDateActive(announcement: Announcement): boolean {
  const now = new Date();

  if (announcement.startDate) {
    const start = new Date(announcement.startDate);
    if (now < start) return false;
  }

  if (announcement.endDate) {
    const end = new Date(announcement.endDate);
    if (now > end) return false;
  }

  return true;
}

/**
 * Check if announcement targets the current role.
 */
function isRoleTargeted(announcement: Announcement, userRole?: UserRole): boolean {
  if (!announcement.targetRoles || announcement.targetRoles.length === 0) {
    return true; // No role restriction
  }
  if (!userRole) {
    return false; // No role and has restrictions
  }
  return announcement.targetRoles.includes(userRole);
}

/**
 * Check if announcement targets the current route.
 */
function isRouteTargeted(announcement: Announcement, currentRoute?: string): boolean {
  if (!announcement.targetRoutes || announcement.targetRoutes.length === 0) {
    return true; // No route restriction
  }
  if (!currentRoute) {
    return false; // No route and has restrictions
  }
  return announcement.targetRoutes.some(route =>
    currentRoute === route || currentRoute.startsWith(route + '/')
  );
}

/**
 * Hook for managing announcements.
 *
 * @example
 * const { activeAnnouncements, dismiss } = useAnnouncements({
 *   userRole: 'CUSTOMER',
 *   currentRoute: '/dashboard',
 *   announcements: announcementsData,
 * });
 *
 * return (
 *   <>
 *     {activeAnnouncements.map(ann => (
 *       <AnnouncementBanner
 *         key={ann.id}
 *         announcement={ann}
 *         onDismiss={() => dismiss(ann.id)}
 *       />
 *     ))}
 *   </>
 * );
 */
export function useAnnouncements({
  userRole,
  currentRoute,
  announcements = [],
}: UseAnnouncementsOptions): UseAnnouncementsReturn {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => getDismissedIds());

  // Sync dismissedIds to localStorage
  useEffect(() => {
    saveDismissedIds(dismissedIds);
  }, [dismissedIds]);

  const dismiss = useCallback((id: string) => {
    setDismissedIds(prev => new Set([...prev, id]));
  }, []);

  const dismissAll = useCallback(() => {
    const currentActive = announcements
      .filter(a => a.enabled && a.dismissible)
      .map(a => a.id);
    setDismissedIds(prev => new Set([...prev, ...currentActive]));
  }, [announcements]);

  const isDismissed = useCallback((id: string) => {
    return dismissedIds.has(id);
  }, [dismissedIds]);

  const clearDismissals = useCallback(() => {
    setDismissedIds(new Set());
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const activeAnnouncements = useMemo(() => {
    return announcements
      .filter(announcement => {
        // Must be enabled
        if (!announcement.enabled) return false;

        // Check date range
        if (!isDateActive(announcement)) return false;

        // Check role targeting
        if (!isRoleTargeted(announcement, userRole)) return false;

        // Check route targeting
        if (!isRouteTargeted(announcement, currentRoute)) return false;

        // Check if dismissed
        if (dismissedIds.has(announcement.id)) return false;

        return true;
      })
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }, [announcements, userRole, currentRoute, dismissedIds]);

  return {
    activeAnnouncements,
    dismiss,
    dismissAll,
    isDismissed,
    clearDismissals,
  };
}

export default useAnnouncements;
